// DEPENDENCIES ---------------------------------------------------------------- //

import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { gql, useApolloClient, useQuery } from '@apollo/client';

import { Box, FormControl, LinearProgress, MenuItem, Select, Typography } from '@mui/material';

import {
  DEALER_LEDGER_VIEW,
  MONEY_COLUMN,
  ProgramClaimsLedger,
  useProgramClaimsSearch,
  eligibilityRulesByContract,
  type ClaimCustomerOption,
  type ClaimsRequest,
  type FetchClaimsPage,
  type MoneyColumnKey,
  type RenderMoveLink,
} from '@hopdrive/feature-program-claims';

import { isProgramWideRole, rooftopClaimsScope, rooftopProgramsWhere, showsWholeProgram } from '@features/programs/programClaims';
import type {
  GetRooftopProgramsQuery,
  GetRooftopProgramsQueryVariables,
  GetRooftopProgramsWithBillingQuery,
  GetRooftopProgramClaimsQuery,
  GetRooftopProgramClaimsQueryVariables,
  GetRooftopProgramClaimsWithHoldQuery,
  GetProgramEligibilityRulesQuery,
  GetProgramEligibilityRulesQueryVariables,
} from './ProgramClaimsSection.generated';

// QUERIES ---------------------------------------------------------------- //
// Row access is enforced server-side by the role's permissions. The where clauses pick the
// rooftop's programs and, within one, the claims its tab lists.

const GET_ROOFTOP_PROGRAMS = gql`
  query getRooftopPrograms($where: programs_bool_exp!) {
    programs(where: $where, order_by: { name: asc }) {
      id
      name
      programtocustomers(order_by: { customer: { name: asc } }) {
        customer {
          id
          name
        }
      }
    }
  }
`;

// The same programs with the customer each one bills, for a program-wide role: the tab reads the
// whole program only on that customer. Hasura does not let the dealer roles read the column, so
// asking for it as one would fail the query.
const GET_ROOFTOP_PROGRAMS_WITH_BILLING = gql`
  query getRooftopProgramsWithBilling($where: programs_bool_exp!) {
    programs(where: $where, order_by: { name: asc }) {
      id
      name
      billable_customer_id
      programtocustomers(order_by: { customer: { name: asc } }) {
        customer {
          id
          name
        }
      }
    }
  }
`;

// The eligibility rules of the program's contracts, so an opened claim's checklist names its rules
// as the Move Planner did ("Genesis vehicle"). A contract declares them in its config and may
// again on each line's coverage rule; only that slice of each config is fetched.
const GET_PROGRAM_ELIGIBILITY_RULES = gql`
  query getProgramEligibilityRules($programId: bigint!) {
    contracts(where: { program_id: { _eq: $programId } }) {
      id
      eligibility: config(path: "$.eligibility")
      contract_rules(where: { schema_key: { _eq: "program-coverage" }, active: { _eq: true } }) {
        id
        eligibility: config(path: "$.eligibility")
      }
    }
  }
`;

// One page of the claims list, in the order the viewer sorted it by. The card asks for each page
// as it is shown, and reads its counts and totals from aggregates over the same where clause.
const GET_ROOFTOP_PROGRAM_CLAIMS = gql`
  query getRooftopProgramClaims($where: program_claims_bool_exp!, $orderBy: [program_claims_order_by!]!, $limit: Int!, $offset: Int!) {
    program_claims(where: $where, order_by: $orderBy, limit: $limit, offset: $offset) {
      id
      move_id
      scenario_id
      contract_id
      eligibility_status
      evaluation_result
      submission_status
      updated_at
      move {
        id
        reference_num
        vehicle_year
        vehicle_make
        vehicle_model
        vehicle_vin
        consumer_name
        pickup_time
        pickup_odometer: pickup_workflow_data(path: "$.odometer")
        delivery_odometer: delivery_workflow_data(path: "$.odometer")
        lane {
          id
          distance_miles
        }
        customer {
          id
          name
        }
      }
      program_ledger(order_by: [{ move_id: asc }, { source_type: asc }, { source_id: asc }, { type: asc }]) {
        id
        move_id
        source_type
        source_id
        type
        program_max
        estimated_amount
        actual_amount
        confirmed
        move {
          id
          accessorials {
            id
            code
          }
        }
      }
    }
  }
`;

// The same page with the claim's review hold, for a program-wide role. The hold names the
// program's own invoices and a re-evaluation HopDrive has not applied, so Hasura lets only the
// program admins (and HopDrive admins) read it; a dealer role asking for it would fail the query.
const GET_ROOFTOP_PROGRAM_CLAIMS_WITH_HOLD = gql`
  query getRooftopProgramClaimsWithHold($where: program_claims_bool_exp!, $orderBy: [program_claims_order_by!]!, $limit: Int!, $offset: Int!) {
    program_claims(where: $where, order_by: $orderBy, limit: $limit, offset: $offset) {
      id
      move_id
      scenario_id
      contract_id
      eligibility_status
      evaluation_result
      submission_status
      review_hold
      updated_at
      move {
        id
        reference_num
        vehicle_year
        vehicle_make
        vehicle_model
        vehicle_vin
        consumer_name
        pickup_time
        pickup_odometer: pickup_workflow_data(path: "$.odometer")
        delivery_odometer: delivery_workflow_data(path: "$.odometer")
        lane {
          id
          distance_miles
        }
        customer {
          id
          name
        }
      }
      program_ledger(order_by: [{ move_id: asc }, { source_type: asc }, { source_id: asc }, { type: asc }]) {
        id
        move_id
        source_type
        source_id
        type
        program_max
        estimated_amount
        actual_amount
        confirmed
        move {
          id
          accessorials {
            id
            code
          }
        }
      }
    }
  }
`;

type ProgramClaim =
  | GetRooftopProgramClaimsQuery['program_claims'][number]
  | GetRooftopProgramClaimsWithHoldQuery['program_claims'][number];
type RooftopProgram = GetRooftopProgramsQuery['programs'][number] | GetRooftopProgramsWithBillingQuery['programs'][number];

const PROGRAM_PICKER_WIDTH = 280;

const renderMoveLink: RenderMoveLink = (moveId, children) => (
  <Box component={Link} to={`/moves/${moveId}`} sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
    {children}
  </Box>
);

// COMPONENT ---------------------------------------------------------------- //

type ProgramClaimsSectionProps = {
  customerId: number;
  role?: string | null;
};

type ProgramClaimsProps = ProgramClaimsSectionProps & { program: RooftopProgram };

/** One program's claims for the rooftop, in the dealer's net view. */
const ProgramClaims = ({ program, customerId, role }: ProgramClaimsProps) => {
  const client = useApolloClient();
  const scope = useMemo(() => rooftopClaimsScope(program.id, customerId, role), [program.id, customerId, role]);
  const search = useProgramClaimsSearch({ scope, view: DEALER_LEDGER_VIEW });

  // A program-wide role also reads each claim's review hold, which the table badges.
  const claimsQuery = isProgramWideRole(role) ? GET_ROOFTOP_PROGRAM_CLAIMS_WITH_HOLD : GET_ROOFTOP_PROGRAM_CLAIMS;
  const fetchPage = useCallback<FetchClaimsPage<ProgramClaim>>(
    ({ where, orderBy, offset, limit }) =>
      client
        .query<GetRooftopProgramClaimsQuery | GetRooftopProgramClaimsWithHoldQuery, GetRooftopProgramClaimsQueryVariables>({
          query: claimsQuery,
          variables: { where, orderBy: [...orderBy], offset, limit },
          fetchPolicy: 'no-cache',
        })
        .then(result => result.data?.program_claims ?? []),
    [client, claimsQuery],
  );
  // The card's counts and totals: aggregates the card writes, over the role's own rows. Hasura
  // must grant the role aggregates on program_claims and program_ledger; without them the query
  // fails and the card says the claims could not be loaded.
  const request = useCallback<ClaimsRequest>(
    (document, variables) => client.query({ query: gql(document), variables, fetchPolicy: 'no-cache' }).then(result => result.data),
    [client],
  );

  // Without the rules (a contract the role can't read, or still loading) the checklist goes by
  // the rules' catalog names.
  const { data: rulesData } = useQuery<GetProgramEligibilityRulesQuery, GetProgramEligibilityRulesQueryVariables>(GET_PROGRAM_ELIGIBILITY_RULES, {
    variables: { programId: program.id },
  });
  const eligibilityRules = useMemo(() => eligibilityRulesByContract(rulesData?.contracts), [rulesData]);

  // Only the whole program has other rooftops' claims in it, so only it gets a rooftop filter.
  const wholeProgram = showsWholeProgram(role, customerId, program);
  const customers = useMemo<ClaimCustomerOption[]>(
    () =>
      wholeProgram
        ? program.programtocustomers.flatMap(({ customer }) => (customer ? [{ id: customer.id, name: customer.name || String(customer.id) }] : []))
        : [],
    [program, wholeProgram],
  );

  return (
    <ProgramClaimsLedger
      search={search}
      fetchPage={fetchPage}
      request={request}
      programName={program.name}
      customers={customers}
      renderMoveLink={renderMoveLink}
      showEligibilityTiles
      singleRooftop={!wholeProgram}
      hideUnusedColumns={wholeProgram ? NO_HIDDEN_COLUMNS : ONE_SIDED_MONEY}
      eligibilityRules={eligibilityRules}
    />
  );
};

// A rooftop's claims sit on one side of the program's money: a Lite dealer is reimbursed and never
// owes, a Full Service dealer may owe and is never reimbursed. A rooftop's list shows only the
// columns its claims use. The whole program mixes both, so it keeps every column.
const ONE_SIDED_MONEY: ReadonlyArray<MoneyColumnKey> = [MONEY_COLUMN.PAID_BACK, MONEY_COLUMN.REIMBURSED];
const NO_HIDDEN_COLUMNS: ReadonlyArray<MoneyColumnKey> = [];

/**
 * The rooftop's program claims and their ledger: covered, copay and reimbursement. A rooftop in
 * more than one program picks which one to read.
 */
const ProgramClaimsSection = ({ customerId, role }: ProgramClaimsSectionProps) => {
  const programsQuery = isProgramWideRole(role) ? GET_ROOFTOP_PROGRAMS_WITH_BILLING : GET_ROOFTOP_PROGRAMS;
  const { data, loading } = useQuery<GetRooftopProgramsQuery | GetRooftopProgramsWithBillingQuery, GetRooftopProgramsQueryVariables>(programsQuery, {
    variables: { where: rooftopProgramsWhere(customerId, role) },
    skip: !customerId,
  });
  const programs = data?.programs ?? [];

  const [chosenProgramId, setChosenProgramId] = useState<number | null>(null);
  // A rooftop switch can leave the chosen program behind, so an unknown choice reads as the first.
  const program = programs.find(candidate => candidate.id === chosenProgramId) ?? programs[0];

  if (loading && !program) return <LinearProgress />;
  if (!program) {
    return (
      <Typography variant='body2' color='text.secondary'>
        This rooftop is not in a program.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {programs.length > 1 && (
        <FormControl size='small' sx={{ width: PROGRAM_PICKER_WIDTH }}>
          <Select
            value={program.id}
            onChange={event => setChosenProgramId(Number(event.target.value))}
            SelectDisplayProps={{ 'aria-label': 'Program' }}
          >
            {programs.map(candidate => (
              <MenuItem key={candidate.id} value={candidate.id}>
                {candidate.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <ProgramClaims key={program.id} program={program} customerId={customerId} role={role} />
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default ProgramClaimsSection;
